
import { Component, Prop, Vue } from 'vue-property-decorator';
import {
  SystemUserInterface,
  SystemUserRepositoryInterface,
} from '@/services/SystemUsers/interfaces';
import { PasswordValidationCallbacks } from '@/services/misc/functions/ValidationFunctions';
import SystemUserLogsDialog from '@/components/dialogs/system-users/SystemUserLogsDialog.vue';

@Component({
  inject: ['systemUserRepository'],
  components: {
    SystemUserLogsDialog,
  },
})
export default class WebsiteUnixPanel extends Vue {
  @Prop({ required: true }) public readonly unixUserId!: number;

  private systemUserRepository!: SystemUserRepositoryInterface;

  public loading = true;
  public saving = false;
  public systemUser: SystemUserInterface | null = null;

  public password = '';
  public passwordConf = '';
  public quota = 0;
  public quotaMultiplier = 1;
  public expireDate: string | null = null;
  public menu: boolean = false;
  public logsDialog = '';

  public quotaMultipliers: Array<{ name: string; value: number }> = [
    { name: 'KiB', value: 1 },
    { name: 'MiB', value: Math.pow(1024, 1) },
    { name: 'GiB', value: Math.pow(1024, 2) },
  ];

  public systemUserStatuses: { [num: number]: string } = {
    0: 'CREATING',
    1: 'CREATED',
    2: 'ERROR',
    3: 'ACTIVE',
    4: 'DISABLED',
  };

  public get statusLabel(): string {
    if (!this.systemUser) {
      return '';
    }
    return this.systemUserStatuses[this.systemUser.status] || String(this.systemUser.status);
  }

  public get statusColor(): string {
    const label = this.statusLabel;
    if (label === 'ACTIVE') {
      return 'success';
    }
    if (label === 'ERROR') {
      return 'error';
    }
    if (label === 'DISABLED') {
      return 'orange';
    }
    return 'grey';
  }

  public get quotaSize(): number {
    return Number((this.systemUser?.quota as any)?.size) || 0;
  }

  public get quotaUsed(): number {
    return Number((this.systemUser?.quota as any)?.used) || 0;
  }

  public get passwordRules() {
    return PasswordValidationCallbacks;
  }

  public get passwordConfRules() {
    return [
      () => this.password === this.passwordConf || 'Passwords do not match',
    ];
  }

  public get quotaRules() {
    return [
      (v: number) => (v !== null && v !== undefined && String(v) !== '') || 'Quota is required',
      (v: number) => v >= 0 || 'Quota must be equal or more than 0',
      (v: number) => {
        const quotaLimit = 200 * Math.pow(1024, 2);
        const currentQuota = Number(v) * this.quotaMultiplier;
        return currentQuota <= quotaLimit || 'Quota exceeds maximum allowed';
      },
    ];
  }

  public get canSave(): boolean {
    if (!this.password || this.password !== this.passwordConf) {
      return false;
    }
    if (this.quota === null || this.quota === undefined || Number(this.quota) < 0) {
      return false;
    }
    return true;
  }

  public async created(): Promise<void> {
    await this.loadUser();
  }

  private async loadUser(): Promise<void> {
    this.loading = true;
    try {
      this.systemUser = await this.systemUserRepository.findSystemUserById(this.unixUserId);
      this.quota = Number(this.systemUser.quota?.size) || 0;
      this.quotaMultiplier = 1;
      this.expireDate = this.systemUser.expireDate
        ? String(this.systemUser.expireDate).slice(0, 10)
        : null;
      this.password = '';
      this.passwordConf = '';
    } catch (err) {
      this.systemUser = null;
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    } finally {
      this.loading = false;
    }
  }

  public async editSystemUser(setQuotaFromPlan: boolean = false): Promise<void> {
    if (!this.systemUser) {
      return;
    }

    this.saving = true;
    try {
      await this.systemUserRepository.editSystemUser(this.systemUser.id, {
        password: this.password,
        passwordConf: this.passwordConf,
        quota: {
          size: Number(this.quota) * this.quotaMultiplier,
        },
        expireDate: this.expireDate as any,
        setQuotaFromPlan,
      });
      this.$successDialog.show('System user update started');
      await this.loadUser();
      this.$emit('updated');
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    } finally {
      this.saving = false;
    }
  }

  public showLogsDialog(): void {
    this.logsDialog = 'system-user-logs-dialog';
  }
}
